import React, { useState, useEffect } from 'react';
import google from '../../icons/google.svg';
import logo from '../../icons/logo.svg';
import '../../style/Login.css';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../context/UserContext";

const ENVIRONMENT = process.env.NODE_ENV || "development";
const SERVER_BASE_URL = ENVIRONMENT === "development" ? "http://localhost:3001" : "";

const Login = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/home";
  const privacyLink = i18n.language === 'en' ? '/privacy-policy-en' : '/privacy-policy';
  const termsLink = i18n.language === 'en' ? '/terms-en' : '/terms';


  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(from);
    }
  }, [user, navigate, from]);

  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const isMobile =
      userAgent.includes("Android") || userAgent.includes("iPhone") || userAgent.includes("iPad");
    const appIdentifiers = ["LinkedInApp", "FBAV/", "FBAN/", "Instagram", "FB_IAB", "MESSENGER"];
    if (isMobile && appIdentifiers.some((app) => userAgent.includes(app))) {
      setIsInAppBrowser(true);
    }
  }, []);

  const handleGoogleLogin = async () => {
    try {
      const oauthUrl = `${SERVER_BASE_URL}/auth/google?redirectTo=${encodeURIComponent(from)}`;
      window.location.assign(oauthUrl);
    } catch (error) {
      console.error("Error during Google login setup:", error);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const lang = i18n.language;
    try {
      const response = await fetch(`${SERVER_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, lang }),
        credentials: "include",
      });

      if (response.ok) {
        navigate("/confirm-email", {
          state: {
            email,
            from,
          },
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || t("home.error"));
      }
    } catch (error) {
      console.error("Error during signup:", error);
      setError(t("home.unexpectedError"));
    }
  };

  return (
    <div className="not-alone-form">
      <div className="content-box">
        <img src={logo} alt="app Logo" className="home-logo" />
        <h1 className="login-logo">{t("home.getStarted")}</h1>
        {!isInAppBrowser && (
          <>
            <div className="login-buttons">
              <button className="login-button google-button" onClick={handleGoogleLogin}>
                <span className="text">{t("home.googleLogin")}</span>
                <img src={google} alt="Google Logo" className="icon" />
              </button>
            </div>
            <div className="divider">
              <span>{t("home.or")}</span>
            </div>
          </>
        )}
        <p className="login-title">{t("home.enterWithCode")}</p>
        <form onSubmit={handleEmailLogin} className="login-buttons login-form">
          <input
            className="login-button"
            dir="ltr"
            type="email"
            placeholder={t("home.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          {error && <p className="error-message">{error}</p>}
          <button type="submit" className="email-button">
            {t("home.emailLogin")}
          </button>
        </form>

        <div className="privacy">
          <Trans
            i18nKey="home.privacy"
            components={{
              1: <Link to={privacyLink} target="_blank" rel="noopener noreferrer" />,
              2: <Link to={termsLink} target="_blank" rel="noopener noreferrer" />,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
